import * as flags from "country-flag-icons/react/3x2";
import React, { ReactNode } from "react";
import reactStringReplace from "react-string-replace";
import * as config from "../../config";
import { MediaKey } from "./Img";

// Uppercase ISO 639-1 Language Codes
export type Language = config.Language;
export type Country = config.Country;
export type Locale = config.Locale;

export const Paths = config.PATH_FOR_LOCALE;
export const LocaleLanguage = config.LANGUAGE_FOR_LOCALE;
export const CountryLocale = config.DEFAULT_LOCALE_FOR_COUNTRY;

export type LocaleByLanguage<Lang extends Language> = keyof {
    [P in keyof typeof LocaleLanguage as (typeof LocaleLanguage)[P] extends Lang ? P : never]: any;
};

export const locPath = <Loc extends Locale>(lan: Loc, str: `/${string}/`) => {
    return Paths[lan] + str;
};

export const languageSwitch = <
    Opts extends Record<Lang, string> | Record<Lang, JSX.Element>,
    Lang extends Language,
    Res extends Opts[keyof Opts] extends JSX.Element ? JSX.Element : string,
>(
    lan: Lang,
    str: Opts,
): Res => {
    const res = str[lan];
    if (typeof res === "string") {
        return reactStringReplace(res, "\n", () => <br />) as unknown as Res;
    } else {
        return res as unknown as Res;
    }
};

export const imageUrlSwitch = <Opts extends Partial<Record<Lang, MediaKey>>, Lang extends Language>(
    lan: Lang,
    _default: MediaKey,
    str?: Opts,
): MediaKey => {
    const res = str ? (str[lan] ?? _default) : _default;
    return res;
};

export const translate = languageSwitch;
export const makeI18n = <Lang extends Language>(lang: Lang) => {
    return {
        i18n: <
            Opts extends Record<Lang, string> | Record<Lang, JSX.Element>,
            Res extends Opts[keyof Opts] extends JSX.Element ? JSX.Element : string,
        >(
            opts: Opts,
        ): Res => languageSwitch(lang, opts),
    };
};

export const genericSwitch = <T extends any, Loc extends string | number | symbol>(
    lan: Loc,
    str: Record<Loc, T>,
): T => {
    return str[lan];
};

/**
 *
 * @deprecated use const { i18n } = makeI18n(...) or translate(...)
 */
export const Loc = <Values extends any, Lang extends Language>(
    props: {
        language: Lang;
    } & Record<Lang, Values>,
): ReactNode => {
    // @ts-ignore
    return <>{props[props.language]}</>;
};

export const isValidLocale = (maybeLocale: string) => {
    return Object.keys(LocaleLanguage).includes(maybeLocale);
};

type P = React.DetailedHTMLProps<React.HTMLAttributes<HTMLOrSVGElement>, HTMLOrSVGElement>;
export const FlagForLocale = (props: { locale: Locale; mode: "country" | "language" } & P) => {
    const Flag =
        props.mode === "language"
            ? flags[config.LANGUAGE_FOR_LOCALE[props.locale] as keyof typeof flags]
            : flags[config.COUNTRY_FOR_LOCALE[props.locale] as keyof typeof flags];
    return <Flag {...props} />;
};
